import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import PropTypes from "prop-types";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import cs from "classnames";

import MyMaorLayout from "../../templates/MyMaorLayout";
import SingleAudioPlayer from "../../templates/AudioPlayer/SingleAudioPlayer";
import { AuthorizationKids, AuthorizationCommon } from "../../organisms/RestrictionModal/RestrictionModal";
import Loader from "../../atoms/Loader";
import { APPLICATION_ROUTES } from "../../../constants";
import { StoreContext } from "../../../contexts";
import { useModal, useQuery } from "../../../hooks";
import { libraryContent } from "../../../activity/content";
import { secureMusicSrc } from "../../../utilities/secureVideo";
import { getDetails } from "../Library/components/Project/components/Preview/utilities";
import placeholder from "../../../assets/images/video-thumbnail-placeholder.png";
import playingIcon from "../../../assets/images/playing.webp";
import whitePlayIcon from "../../../assets/svg/white-play.svg";
import audioPageShadow from "../../../assets/images/audio-page-shadow.png";
import classes from "./AudioLibraryPage.module.scss";

// ── Category card ─────────────────────────────────────────────────────────────
const CategoryCard = ({ category, onClick }) => (
  <div
    className={cs(classes.categoryCard)}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === "Enter" && onClick()}
  >
    <div
      className={classes.categoryThumb}
      style={{ backgroundImage: `url(${category.image_url || placeholder})` }}
    >
      {category.coming_soon && (
        <>
          <div className={classes.comingSoonOverlay}></div>
          <div className={classes.comingSoonRibbon}>Coming Soon</div>
        </>
      )}
    </div>
    <p className={classes.categoryName}>{category.name_english}</p>
    <p className={classes.categoryCount}>{category?.count} Stories</p>
  </div>
);

CategoryCard.propTypes = {
  category: PropTypes.shape({
    image_url: PropTypes.string,
    coming_soon: PropTypes.bool,
    name_english: PropTypes.string,
    count: PropTypes.number,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const CATEGORY_THEMES = [
  { main: "#c74d58", icon: "#8f373f" },
  { main: "#7d4c58", icon: "#643c46" },
  { main: "#215b79", icon: "#194257" }
];

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// A restricted episode comes without a link: the player gets it from the API when it loads
// the episode, and on401/on403 open the popups when the member has no access
const buildPlaylist = (category, { on401, on403 }) =>
  (category.videos || []).map((item) => {
    const d = getDetails("en", item.locales);
    return {
      musicSrc: secureMusicSrc({ details: d, on401, on403 }),
      name: d.name || "",
      cover: d.thumbnail || item.thumbnail || category.image_url || placeholder,
      singer: category.name_english,
      duration: d.duration_formatted || "",
      number: d.number,
      activity: libraryContent("audio", d),
    };
  });

// An episode's slug is its video_url, the path it is shared at (/sa101), which the /:id
// route sends here as ?audio=sa101 - or its share code, the slug base64-encoded
const decodeShareCode = (slug) => {
  try {
    return atob(slug).toLowerCase();
  } catch (err) {
    return null;
  }
};

const hasSlug = (episode, slug) => {
  const slugs = [slug.toLowerCase(), decodeShareCode(slug)];
  return Object.values(episode.locales || {}).some(
    (locale) => locale?.video_url && slugs.includes(locale.video_url.toLowerCase())
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const AudioLibraryPage = observer(() => {
  const { library, authorization, partnership } = useContext(StoreContext);
  const history = useHistory();
  const linkedSlug = useQuery().get("audio");
  // As on the library page: 401 asks the visitor to sign in, 403 to become a member
  const signInPopup = useModal();
  const membershipPopup = useModal();
  const popups = { on401: signInPopup.onOpen, on403: membershipPopup.onOpen };

  const [playingCategory, setPlayingCategory] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [isPaused, setIsPaused] = useState(true);
  const [autoPlay, setAutoPlay] = useState(true);
  const audioInstanceRef = useRef(null);
  const linkedSlugHandled = useRef(false);

  const audioCategories = library.filterData.categories.data?.Audio || [];
  const isCatsLoading = library.filterData.categories.isLoading;
  const playableCategories = audioCategories.filter(cat => !cat.coming_soon);

  const activeCategoryIndex = playingCategory
    ? playableCategories.findIndex(c => c.id === playingCategory)
    : 0;
  const activeTheme = CATEGORY_THEMES[Math.max(0, activeCategoryIndex) % CATEGORY_THEMES.length] || CATEGORY_THEMES[0];

  useEffect(() => {
    library.getCategories();
  }, []);

  // Opened from an audio's link: load it in the player, paused, and scroll to it
  useEffect(() => {
    if (!linkedSlug || linkedSlugHandled.current || isCatsLoading) return;
    linkedSlugHandled.current = true;
    if (library.filterData.categories.isError) return;

    const category = playableCategories.find((cat) =>
      (cat.videos || []).some((ep) => hasSlug(ep, linkedSlug))
    );
    if (!category) {
      history.replace(APPLICATION_ROUTES.NOT_FOUND);
      return;
    }
    const index = category.videos.findIndex((ep) => hasSlug(ep, linkedSlug));

    setPlayingCategory(category.id);
    setPlaylist(buildPlaylist(category, popups));
    setCurrentIndex(index);
    setAutoPlay(false);
    document
      .getElementById(`audio-episode-${category.id}-${index}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [linkedSlug, isCatsLoading]);

  useEffect(() => {
    document.documentElement.style.setProperty("--playerPrimary", activeTheme.icon);
    document.documentElement.style.setProperty("--playerSecondary", activeTheme.main);
    return () => {
      document.documentElement.style.removeProperty("--playerPrimary");
      document.documentElement.style.removeProperty("--playerSecondary");
    };
  }, [activeTheme.icon, activeTheme.main]);

  const handleCategoryClick = useCallback((categoryId) => {
    const el = document.getElementById(`audio-category-${categoryId}`);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, []);

  const handlePlay = useCallback(async (category, localIndex) => {
    const audioEl = document.querySelector(".music-player-audio");

    // Paused (or loaded from a link without playing yet), the episode's button plays it
    if (playingCategory === category.id && currentIndex === localIndex && !isPaused) {
      audioEl?.pause();
      return;
    }

    const isSameCategory = playingCategory === category.id;
    const tracks = isSameCategory ? playlist : buildPlaylist(category, popups);

    // Like a library video, the API checks the member's access to the episode before it
    // plays (the link it answers is kept for the player); without access a popup says so
    const { musicSrc } = tracks[localIndex];
    if (typeof musicSrc === "function") {
      try {
        await musicSrc();
      } catch {
        return;
      }
    }

    // After the await React 17 no longer batches these, and the player must get the new
    // playlist and its index together
    unstable_batchedUpdates(() => {
      if (!isSameCategory) {
        setPlayingCategory(category.id);
        setPlaylist(tracks);
      }
      setAutoPlay(true);
      setCurrentIndex(localIndex);
    });

    setTimeout(() => {
      if (audioInstanceRef.current?.playByIndex) {
        audioInstanceRef.current.playByIndex(localIndex);
      }
      const newAudioEl = document.querySelector(".music-player-audio");
      newAudioEl?.play().catch(() => { });
    }, isSameCategory ? 50 : 250);
  }, [playingCategory, currentIndex, isPaused, playlist]);

  return (
    <MyMaorLayout>
      <div className={classes.pageWrapper}>
        <div className={classes.container} style={{ paddingBottom: 0 }}>
          <h1 className={classes.pageTitle}>Audio Library</h1>

          {/* ── Category row ── */}
          <div className={classes.categoryRow}>
            {isCatsLoading ? (
              <Loader />
            ) : audioCategories.length === 0 ? (
              <p className={classes.empty}>No audio categories available.</p>
            ) : (
              audioCategories.map((cat) => (
                <CategoryCard
                  key={cat.id}
                  category={cat}
                  onClick={() => handleCategoryClick(cat.id)}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Shadow Image ── */}
        {!isCatsLoading && audioCategories.length > 0 && (
          <div className={classes.shadowDivider}>
            <img src={audioPageShadow} alt="" />
          </div>
        )}

        {/* ── Episode panels ── */}
        <div className={cs(classes.container, classes.allPanels, playlist.length > 0 && classes.hasPlayer)} style={{ paddingTop: 0 }}>
          {!isCatsLoading && playableCategories.map((category, catIndex) => {
            const theme = CATEGORY_THEMES[catIndex % CATEGORY_THEMES.length] || CATEGORY_THEMES[0];
            const episodes = category.videos || [];

            return (
              <div
                key={category.id}
                id={`audio-category-${category.id}`}
                className={classes.episodePanel}
                style={{
                  "--cat-color": theme.main,
                  "--cat-border": hexToRgba(theme.main, 1),
                  "--icon-color": theme.icon,
                }}
              >
                <div className={classes.panelHeader}>
                  <h3 className={classes.panelTitle}>{category.name_english}</h3>
                  {category.item_purchase_link && (
                    <a
                      href={category.item_purchase_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.buyBtn}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM5.8 6H20l-1.7 9H7.7L5.8 6zM3.4 2H1v2h2l3.6 7.6L5.2 14C5.1 14.3 5 14.6 5 15c0 1.1.9 2 2 2h13v-2H7.4c-.1 0-.2-.1-.2-.2l.1-.3.9-1.5H18c.7 0 1.4-.4 1.7-1l3.4-6.2c.4-.7-.1-1.8-1-1.8H5.2L4.3 2H3.4z" />
                      </svg>
                      <span className={classes.buyBtnText}>
                        {category.item_label || "Buy Book"}
                      </span>
                    </a>
                  )}
                </div>

                {episodes.length === 0 ? (
                  <p className={classes.emptyEpisodes}>No episodes found for this category.</p>
                ) : (
                  <div className={classes.episodeList}>
                    {episodes.map((ep, index) => {
                      const d = getDetails("en", ep.locales);
                      const isCurrent = playingCategory === category.id && currentIndex === index;
                      const isPlaying = isCurrent && !isPaused;
                      return (
                        <div
                          key={ep.id || index}
                          id={`audio-episode-${category.id}-${index}`}
                          className={cs(classes.episodeRow, isCurrent && classes.episodePlaying)}
                        >
                          <span className={classes.episodeName}>
                            {/* {d.number ? `Episode ${d.number} | ` : ""} */}
                            <strong>{d.name}</strong>
                          </span>
                          <span className={classes.episodeDuration}>
                            {d.duration_formatted || ""}
                          </span>
                          <button
                            className={cs(
                              classes.episodePlayBtn,
                              isPlaying && classes.episodePlayBtnActive
                            )}
                            onClick={() => handlePlay(category, index)}
                            aria-label={isPlaying ? "Now playing" : `Play episode ${index + 1}`}
                          >
                            {isPlaying
                              ? <img src={playingIcon} alt="playing" width="20" height="20" />
                              : <img src={whitePlayIcon} alt="play" width="18" height="18" />
                            }
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Audio player bar ── */}
        {playlist.length > 0 && (
          <div>
            <SingleAudioPlayer
              playlist={playlist}
              playIndex={currentIndex || 0}
              autoPlay={autoPlay}
              onAudioInstance={(instance) => {
                audioInstanceRef.current = instance;
                instance.addEventListener("play", () => setIsPaused(false));
                instance.addEventListener("pause", () => setIsPaused(true));
              }}
            />
          </div>
        )}

        <AuthorizationKids
          popup={signInPopup}
          authorization={authorization}
          partnership={partnership}
        />
        <AuthorizationCommon
          popup={membershipPopup}
          authorization={authorization}
          partnership={partnership}
        />
      </div>
    </MyMaorLayout>
  );
});

export default AudioLibraryPage;
